import React, { ComponentType, PropsWithChildren, useCallback, useMemo } from 'react';
import { useAppSelector } from '../../hooks/ReduxHooks';
import { useActions } from '../../hooks/useActions';
import { sendAFormPreviewFieldClicked } from '../../sendaform/reduxActions';
import { FormEditorView } from '../formeditor-types';
import { getSendAFormState } from '../selectors';

export type SendAFormInteractive = {
  /**
   * `true`, if the form is presented from the normal form editor view, `false` if it is presented from the Send A Form V2 preview.
   *
   * ---
   *
   * Send A Form V2 editor previews appear as if they're editable, but not actually editable.
   * This should be `true` by default to maintain existing functionality ad  `false` when it's
   * presented from the Send a Form V2 modal.
   */
  interactive?: boolean;

  /** True, if the the field is selected, false otherwise. */
  sendAFormSelected?: boolean;
  /** The selected field color. */
  highlightColor?: string;

  /**
   * Raised when a field is clicked in the form view.
   *
   * We want to handle this separately from the normal form editor field click event.
   * This event will dispatch a new action to let the send a form v2 feature know that a field was clicked.
   *
   * @param fieldId The id of the field that was clicked.
   */
  onSendAFormFieldClick?: (fieldId: string, viewType: FormEditorView) => void;
};

/**
 * This HOC applies the interactive state of the send a form v2 feature to the wrapped component.
 */
export function withSendAFormInteractiveState(WrappedComponent: ComponentType<unknown>) {
  return function EnhancedComponent(props: ExplicitAny) {
    const { interactive, selectedFieldPositionIds, highlightColor } = useAppSelector((state) => getSendAFormState(state));
    const sendAFormSelected = useMemo(() => selectedFieldPositionIds?.includes(props?.fieldId), [selectedFieldPositionIds, props?.fieldId]);
    const fieldClickAction: (fieldId: string, viewType: FormEditorView) => void = useActions(sendAFormPreviewFieldClicked);

    const handleFieldClick = useCallback(
      (fieldId: string, viewType: FormEditorView) => {
        // dispatch an action to let the send a form v2 feature know that a field was clicked
        fieldClickAction(fieldId, viewType);
      },
      [fieldClickAction]
    );

    return (
      <WrappedComponent
        interactive={interactive}
        sendAFormSelected={sendAFormSelected}
        highlightColor={highlightColor}
        onSendAFormFieldClick={handleFieldClick}
        {...props}
      />
    );
  };
}

type ApplySendAFormInteractiveStateProps = PropsWithChildren<unknown>;

/**
 * This component clones it's children and applies the send a form interactive state as
 * a prop. This is used in the preview of send a form v2 flow to disable interactions with
 * the form/list view, but retains its appearance.
 */
export function ApplySendAFormInteractiveState({ children }: ApplySendAFormInteractiveStateProps) {
  const { interactive, selectedFieldPositionIds } = useAppSelector((state) => getSendAFormState(state));

  if (!children) {
    return null;
  }

  // clone the children and apply the interactive state to them
  const childrenWithProps = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) {
      return child as React.ReactElement;
    }

    const sendAFormSelected = selectedFieldPositionIds?.includes(child.props?.fieldId);
    const props = { ...child.props, interactive, sendAFormSelected };
    const clonedChild = React.cloneElement(child, props);

    return clonedChild;
  });

  return <>{childrenWithProps}</>;
}
